import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Collapse, Form, Row, Spinner, Table } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";
import useAutoLogout from "../autologout";

const ADMIN_ROLE = "1";
const SERVICE_KEYS = ["pet", "ct", "spect", "therapy", "eecp"];
const CHART_COLORS = [
  [15, 118, 110],
  [37, 99, 235],
  [168, 85, 247],
  [220, 38, 38],
  [217, 119, 6],
  [22, 163, 74],
];

function pad2(value) {
  return String(value).padStart(2, "0");
}

function parseDateInput(value) {
  if (!value) return null;
  const source = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(source.getTime())) return null;
  return new Date(source.getFullYear(), source.getMonth(), source.getDate());
}

function toDateInput(value) {
  const date = parseDateInput(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function toMonthInput(value) {
  const date = parseDateInput(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

function getMondayOf(dateLike) {
  const date = parseDateInput(dateLike);
  if (!date) return "";
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return toDateInput(date);
}

function getSundayOf(mondayLike) {
  const date = parseDateInput(mondayLike);
  if (!date) return "";
  date.setDate(date.getDate() + 6);
  return toDateInput(date);
}

function getMonthBounds(monthValue) {
  if (!monthValue) return { start: "", end: "" };
  const [yearStr, monthStr] = String(monthValue).split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    return { start: "", end: "" };
  }
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return { start: toDateInput(start), end: toDateInput(end) };
}

function addDays(dateInput, days) {
  const date = parseDateInput(dateInput);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return toDateInput(date);
}

function getMonthLabel(monthValue) {
  const bounds = getMonthBounds(monthValue);
  if (!bounds.start) return "-";
  const date = parseDateInput(bounds.start);
  return date
    ? date.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : monthValue;
}

function formatDate(value) {
  if (!value) return "-";
  const date = parseDateInput(value);
  if (!date) return String(value);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatShortDate(value) {
  if (!value) return "-";
  const date = parseDateInput(value);
  if (!date) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatCurrency(amount) {
  return Number(amount ?? 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function formatCount(value) {
  return Number(value ?? 0).toLocaleString("en-IN");
}

function numericValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDoctorHospitalLabel(doctor) {
  if (Array.isArray(doctor?.hospitals)) {
    return doctor.hospitals.filter(Boolean).join(", ");
  }
  return doctor?.hospital ?? doctor?.hospital_name ?? "";
}

function createEmptyServiceTotals() {
  return {
    test_count: 0,
    pet_count: 0,
    pet_amount: 0,
    ct_count: 0,
    ct_amount: 0,
    spect_count: 0,
    spect_amount: 0,
    therapy_count: 0,
    therapy_amount: 0,
    eecp_count: 0,
    eecp_amount: 0,
  };
}

function createEmptySummary() {
  return {
    total_amount: 0,
    total_bills: 0,
    staff_count: 0,
    doctor_count: 0,
    week_count: 0,
    average_bill_value: 0,
  };
}

function createEmptyReport(mode, periodValue) {
  return {
    mode,
    periodValue,
    periodLabel: "-",
    week_start: "",
    week_end: "",
    month_start: "",
    month_end: "",
    weeks: [],
    staff: [],
    serviceTotals: createEmptyServiceTotals(),
    summary: createEmptySummary(),
  };
}

function addServiceTotals(target, source) {
  target.test_count += numericValue(source?.test_count);
  target.pet_count += numericValue(source?.pet_count);
  target.pet_amount += numericValue(source?.pet_amount);
  target.ct_count += numericValue(source?.ct_count);
  target.ct_amount += numericValue(source?.ct_amount);
  target.spect_count += numericValue(source?.spect_count);
  target.spect_amount += numericValue(source?.spect_amount);
  target.therapy_count += numericValue(source?.therapy_count);
  target.therapy_amount += numericValue(source?.therapy_amount);
  target.eecp_count += numericValue(source?.eecp_count);
  target.eecp_amount += numericValue(source?.eecp_amount);
}

function createDoctorBucket(row) {
  return {
    doctor_id: row.doctor_id,
    doctor_name: row.doctor_name,
    hospital: row.hospital,
    total_amount: 0,
    total_bills: 0,
    test_count: 0,
    pet_count: 0,
    pet_amount: 0,
    ct_count: 0,
    ct_amount: 0,
    spect_count: 0,
    spect_amount: 0,
    therapy_count: 0,
    therapy_amount: 0,
    eecp_count: 0,
    eecp_amount: 0,
  };
}

function createStaffBucket(row) {
  return {
    staff_id: row.staff_id,
    staff_name: row.staff_name,
    staff_code: row.staff_code,
    total_amount: 0,
    total_bills: 0,
    doctors: [],
  };
}

function accumulateDoctorBucket(doctor, row) {
  const amount = numericValue(row.total_amount);
  const bills = numericValue(row.total_bills);
  const testCount = numericValue(row.test_count);
  const petCount = numericValue(row.pet_count);
  const petAmount = numericValue(row.pet_amount);
  const ctCount = numericValue(row.ct_count);
  const ctAmount = numericValue(row.ct_amount);
  const spectCount = numericValue(row.spect_count);
  const spectAmount = numericValue(row.spect_amount);
  const therapyCount = numericValue(row.therapy_count);
  const therapyAmount = numericValue(row.therapy_amount);
  const eecpCount = numericValue(row.eecp_count);
  const eecpAmount = numericValue(row.eecp_amount);

  doctor.total_amount += amount;
  doctor.total_bills += bills;
  doctor.test_count += testCount;
  doctor.pet_count += petCount;
  doctor.pet_amount += petAmount;
  doctor.ct_count += ctCount;
  doctor.ct_amount += ctAmount;
  doctor.spect_count += spectCount;
  doctor.spect_amount += spectAmount;
  doctor.therapy_count += therapyCount;
  doctor.therapy_amount += therapyAmount;
  doctor.eecp_count += eecpCount;
  doctor.eecp_amount += eecpAmount;
}

function accumulateStaffBucket(staff, row) {
  staff.total_amount += numericValue(row.total_amount);
  staff.total_bills += numericValue(row.total_bills);
}

function summarizeWeeklyResponse(response, weekFallback) {
  const staffRows = Array.isArray(response?.staff) ? response.staff : [];
  const weekStart = response?.week_start ?? weekFallback ?? "";
  const weekEnd = response?.week_end ?? getSundayOf(weekStart) ?? "";

  let totalAmount = 0;
  let totalBills = 0;

  for (const staff of staffRows) {
    totalAmount += numericValue(staff.total_amount);
    totalBills += numericValue(staff.total_bills);
  }

  return {
    week_start: weekStart,
    week_end: weekEnd,
    total_amount: totalAmount,
    total_bills: totalBills,
    staff_count: staffRows.length,
  };
}

function buildMonthlyWeekStarts(monthValue) {
  const bounds = getMonthBounds(monthValue);
  if (!bounds.start || !bounds.end) return [];

  const cursorStart = getMondayOf(bounds.start);
  const cursorEnd = getMondayOf(bounds.end);
  if (!cursorStart || !cursorEnd) return [];

  const weekStarts = [];
  let cursor = cursorStart;
  while (cursor <= cursorEnd) {
    weekStarts.push(cursor);
    cursor = addDays(cursor, 7);
  }
  return weekStarts;
}

function fitLabel(doc, label, maxWidth) {
  const text = String(label ?? "");
  if (doc.getTextWidth(text) <= maxWidth) return text;

  let output = text;
  while (output.length > 0 && doc.getTextWidth(`${output}...`) > maxWidth) {
    output = output.slice(0, -1);
  }
  return `${output}...`;
}

function makeComparisonSeries(reportData, selectedStaffId) {
  if (!reportData?.staff?.length) return [];
  const staffRows = [...reportData.staff].sort((a, b) => numericValue(b.total_amount) - numericValue(a.total_amount));

  if (selectedStaffId === "all") {
    return staffRows.slice(0, 8).map((staff, index) => ({
      label: staff.staff_name || staff.staff_code || `Staff ${staff.staff_id}`,
      value: numericValue(staff.total_amount),
      color: CHART_COLORS[index % CHART_COLORS.length],
      meta: formatCount(staff.total_bills),
    }));
  }

  const staff = staffRows[0];
  if (!staff) return [];
  return [...(staff.doctors || [])]
    .sort((a, b) => numericValue(b.total_amount) - numericValue(a.total_amount))
    .slice(0, 8)
    .map((doctor, index) => ({
      label: doctor.doctor_name || `Doctor ${doctor.doctor_id}`,
      value: numericValue(doctor.total_amount),
      color: CHART_COLORS[index % CHART_COLORS.length],
      meta: formatCount(doctor.total_bills),
    }));
}

function makeTrendSeries(reportData, reportMode) {
  if (!reportData) return [];

  if (reportMode === "monthly") {
    return (reportData.weeks || []).map((week, index) => ({
      label: `${formatShortDate(week.week_start)} - ${formatShortDate(week.week_end)}`,
      value: numericValue(week.total_amount),
      color: CHART_COLORS[index % CHART_COLORS.length],
      meta: formatCount(week.total_bills),
    }));
  }

  const serviceTotals = reportData.serviceTotals || createEmptyServiceTotals();
  return [
    { label: "PET", value: numericValue(serviceTotals.pet_amount), color: CHART_COLORS[0], meta: formatCount(serviceTotals.pet_count) },
    { label: "CT", value: numericValue(serviceTotals.ct_amount), color: CHART_COLORS[1], meta: formatCount(serviceTotals.ct_count) },
    { label: "SPECT", value: numericValue(serviceTotals.spect_amount), color: CHART_COLORS[2], meta: formatCount(serviceTotals.spect_count) },
    { label: "Therapy", value: numericValue(serviceTotals.therapy_amount), color: CHART_COLORS[3], meta: formatCount(serviceTotals.therapy_count) },
    { label: "EECP", value: numericValue(serviceTotals.eecp_amount), color: CHART_COLORS[4], meta: formatCount(serviceTotals.eecp_count) },
  ];
}

function buildMonthlyReport(responses, monthValue, selectedStaffId) {
  const report = createEmptyReport("monthly", monthValue);
  const staffMap = new Map();
  const serviceTotals = createEmptyServiceTotals();
  const weeks = [];

  for (const response of responses) {
    const weekSummary = summarizeWeeklyResponse(response, response?.week_start);
    weeks.push(weekSummary);

    for (const staffRow of Array.isArray(response?.staff) ? response.staff : []) {
      if (!staffMap.has(staffRow.staff_id)) {
        staffMap.set(staffRow.staff_id, createStaffBucket(staffRow));
      }

      const staff = staffMap.get(staffRow.staff_id);
      accumulateStaffBucket(staff, staffRow);

      for (const doctorRow of Array.isArray(staffRow.doctors) ? staffRow.doctors : []) {
        let doctor = staff.doctors.find((item) => String(item.doctor_id) === String(doctorRow.doctor_id));
        if (!doctor) {
          doctor = createDoctorBucket(doctorRow);
          staff.doctors.push(doctor);
        }
        accumulateDoctorBucket(doctor, doctorRow);
        addServiceTotals(serviceTotals, doctorRow);
      }
    }
  }

  const staffList = [...staffMap.values()].map((staff) => ({
    ...staff,
    doctors: [...staff.doctors].sort((a, b) => String(a.doctor_name || "").localeCompare(String(b.doctor_name || ""))),
  }));

  const filteredStaff = selectedStaffId === "all"
    ? staffList
    : staffList.filter((staff) => String(staff.staff_id) === String(selectedStaffId));

  report.weeks = weeks;
  report.staff = filteredStaff;
  report.serviceTotals = serviceTotals;
  report.summary = {
    total_amount: weeks.reduce((sum, week) => sum + numericValue(week.total_amount), 0),
    total_bills: weeks.reduce((sum, week) => sum + numericValue(week.total_bills), 0),
    staff_count: filteredStaff.length,
    doctor_count: filteredStaff.reduce((sum, staff) => sum + (staff.doctors?.length || 0), 0),
    week_count: weeks.length,
    average_bill_value: 0,
  };

  report.summary.average_bill_value = report.summary.total_bills > 0
    ? report.summary.total_amount / report.summary.total_bills
    : 0;

  report.periodLabel = getMonthLabel(monthValue);
  const bounds = getMonthBounds(monthValue);
  report.month_start = bounds.start;
  report.month_end = bounds.end;

  return report;
}

function buildWeeklyReport(response, weekStart, selectedStaffId) {
  const report = createEmptyReport("weekly", weekStart);
  const staffRows = Array.isArray(response?.staff) ? response.staff : [];
  const staffMap = new Map();
  const serviceTotals = createEmptyServiceTotals();

  for (const staffRow of staffRows) {
    if (!staffMap.has(staffRow.staff_id)) {
      staffMap.set(staffRow.staff_id, createStaffBucket(staffRow));
    }

    const staff = staffMap.get(staffRow.staff_id);
    accumulateStaffBucket(staff, staffRow);

    for (const doctorRow of Array.isArray(staffRow.doctors) ? staffRow.doctors : []) {
      let doctor = staff.doctors.find((item) => String(item.doctor_id) === String(doctorRow.doctor_id));
      if (!doctor) {
        doctor = createDoctorBucket(doctorRow);
        staff.doctors.push(doctor);
      }
      accumulateDoctorBucket(doctor, doctorRow);
      addServiceTotals(serviceTotals, doctorRow);
    }
  }

  const staffList = [...staffMap.values()].map((staff) => ({
    ...staff,
    doctors: [...staff.doctors].sort((a, b) => String(a.doctor_name || "").localeCompare(String(b.doctor_name || ""))),
  }));

  const filteredStaff = selectedStaffId === "all"
    ? staffList
    : staffList.filter((staff) => String(staff.staff_id) === String(selectedStaffId));

  const weekEnd = response?.week_end ?? getSundayOf(weekStart);
  const weeklySummary = summarizeWeeklyResponse(response, weekStart);
  report.week_start = weekStart;
  report.week_end = weekEnd;
  report.weeks = [weeklySummary];
  report.staff = filteredStaff;
  report.serviceTotals = serviceTotals;
  report.summary = {
    total_amount: weeklySummary.total_amount,
    total_bills: weeklySummary.total_bills,
    staff_count: filteredStaff.length,
    doctor_count: filteredStaff.reduce((sum, staff) => sum + (staff.doctors?.length || 0), 0),
    week_count: 1,
    average_bill_value: weeklySummary.total_bills > 0 ? weeklySummary.total_amount / weeklySummary.total_bills : 0,
  };
  report.periodLabel = `Week of ${formatDate(weekStart)} to ${formatDate(weekEnd)}`;
  return report;
}

function drawBarPanel(doc, {
  x,
  y,
  width,
  title,
  subtitle,
  series,
  emptyText = "No chart data available.",
  accent = [15, 118, 110],
}) {
  const padding = 5;
  const headerHeight = subtitle ? 12 : 9;
  const labelWidth = Math.max(34, Math.min(66, width * 0.28));
  const valueWidth = 26;
  const barAreaWidth = width - labelWidth - valueWidth - padding * 2;
  const rowHeight = 9;
  const innerPaddingTop = 4;
  const items = (series || []).filter((item) => Number.isFinite(Number(item.value)));
  const rows = Math.min(items.length, 6);
  const contentHeight = rows > 0 ? rows * rowHeight + (rows - 1) * 3 : 12;
  const panelHeight = headerHeight + contentHeight + 14;

  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, width, panelHeight, 3, 3, "FD");

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(title, x + padding, y + 8);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(subtitle, x + padding, y + 13);
  }

  if (rows === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(emptyText, x + padding, y + panelHeight - 7);
    return panelHeight;
  }

  const maxValue = Math.max(...items.slice(0, 6).map((item) => numericValue(item.value)), 1);
  let currentY = y + headerHeight + innerPaddingTop;

  items.slice(0, 6).forEach((item, index) => {
    const barValue = numericValue(item.value);
    const barWidth = Math.max(1.5, (barAreaWidth * barValue) / maxValue);
    const color = item.color || accent;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    doc.setTextColor(51, 65, 85);
    doc.text(fitLabel(doc, item.label, labelWidth - 2), x + padding, currentY + 5.5);

    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x + labelWidth + padding, currentY + 1.2, barAreaWidth, 5.2, 2, 2, "FD");

    doc.setFillColor(color[0], color[1], color[2]);
    doc.roundedRect(x + labelWidth + padding, currentY + 1.2, barWidth, 5.2, 2, 2, "F");

    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.text(String(item.meta ?? formatCurrency(barValue)), x + width - valueWidth + 2, currentY + 5.5, { align: "right" });

    currentY += rowHeight;
    if (index < Math.min(items.length, 6) - 1) {
      currentY += 1.5;
    }
  });

  return panelHeight;
}

function BillingReport() {
  useAutoLogout();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const roleid = localStorage.getItem("roleid");
  const isAdmin = roleid === ADMIN_ROLE;

  const today = new Date();
  const [reportMode, setReportMode] = useState("weekly");
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMondayOf(today));
  const [selectedMonth, setSelectedMonth] = useState(toMonthInput(today));
  const [syncWeekStart, setSyncWeekStart] = useState(getMondayOf(today));
  const [selectedStaffId, setSelectedStaffId] = useState("all");
  const [staffList, setStaffList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState("");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: baseurladdress,
        headers: { Authorization: `Bearer ${token}` },
      }),
    [token]
  );

  useEffect(() => {
    if (!isAdmin) {
      navigate("/unauthorized");
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!token) return;

    const loadStaff = async () => {
      try {
        const res = await axios.get(`${baseurladdress}staff/findallstaff`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data?.status === "Success" ? res.data.staff || [] : res.data?.staff || [];
        setStaffList(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("findallstaff error:", err);
        setStaffList([]);
      }
    };

    loadStaff();
  }, [token]);

  const staffLabel = useMemo(() => {
    if (selectedStaffId === "all") return "All Staff";
    const staff = staffList.find((item) => String(item.staff_id) === String(selectedStaffId));
    return staff ? `${staff.staff_name}${staff.staff_code ? ` (${staff.staff_code})` : ""}` : `Staff ${selectedStaffId}`;
  }, [selectedStaffId, staffList]);

  const comparisonSeries = useMemo(() => makeComparisonSeries(reportData, selectedStaffId), [reportData, selectedStaffId]);
  const trendSeries = useMemo(() => makeTrendSeries(reportData, reportMode), [reportData, reportMode]);
  const summary = reportData?.summary || createEmptySummary();
  const serviceTotals = reportData?.serviceTotals || createEmptyServiceTotals();

  async function loadWeeklyReport(weekStart, staffId = selectedStaffId) {
    if (!token || !isAdmin) return;

    setLoading(true);
    setError("");
    setExpanded({});

    try {
      const params = new URLSearchParams();
      params.set("week_start", weekStart);
      if (staffId && staffId !== "all") {
        params.set("staff_id", staffId);
      }

      const res = await api.get(`billing/report?${params.toString()}`);
      const nextReport = buildWeeklyReport(res.data, weekStart, staffId);
      setReportData(nextReport);
    } catch (err) {
      console.error("billing weekly report error:", err);
      setReportData(null);
      setError(err.response?.data?.message || "Unable to load the weekly billing report.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMonthlyReport(monthValue, staffId = selectedStaffId) {
    if (!token || !isAdmin) return;

    setLoading(true);
    setError("");
    setExpanded({});

    try {
      const weekStarts = buildMonthlyWeekStarts(monthValue);
      const responses = await Promise.all(
        weekStarts.map(async (weekStart) => {
          const params = new URLSearchParams();
          params.set("week_start", weekStart);
          if (staffId && staffId !== "all") {
            params.set("staff_id", staffId);
          }
          const res = await api.get(`billing/report?${params.toString()}`);
          return res.data;
        })
      );

      const nextReport = buildMonthlyReport(responses, monthValue, staffId);
      setReportData(nextReport);
    } catch (err) {
      console.error("billing monthly report error:", err);
      setReportData(null);
      setError(err.response?.data?.message || "Unable to load the monthly billing report.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!token || !isAdmin || reportMode !== "weekly") return;
    loadWeeklyReport(selectedWeekStart, selectedStaffId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin, reportMode, selectedWeekStart, selectedStaffId]);

  useEffect(() => {
    if (!token || !isAdmin || reportMode !== "monthly") return;
    loadMonthlyReport(selectedMonth, selectedStaffId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin, reportMode, selectedMonth, selectedStaffId]);

  if (!isAdmin) {
    return null;
  }

  function toggleExpand(staffId) {
    setExpanded((prev) => ({ ...prev, [staffId]: !prev[staffId] }));
  }

  function handleExportExcel() {
    if (!reportData?.staff?.length) return;

    const rows = [];
    reportData.staff.forEach((staff) => {
      (staff.doctors || []).forEach((doctor) => {
        rows.push({
          Scope: selectedStaffId === "all" ? "All staff" : staffLabel,
          Mode: reportMode,
          Period: reportData.periodLabel,
          "Staff Code": staff.staff_code,
          "Staff Name": staff.staff_name,
          "Doctor Name": doctor.doctor_name,
          Hospital: formatDoctorHospitalLabel(doctor),
          "Total Bills": doctor.total_bills,
          "Total Amount": doctor.total_amount,
          Tests: doctor.test_count,
          PET: doctor.pet_count,
          "PET Amount": doctor.pet_amount,
          CT: doctor.ct_count,
          "CT Amount": doctor.ct_amount,
          SPECT: doctor.spect_count,
          "SPECT Amount": doctor.spect_amount,
          Therapy: doctor.therapy_count,
          "Therapy Amount": doctor.therapy_amount,
          EECP: doctor.eecp_count,
          "EECP Amount": doctor.eecp_amount,
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Billing Report");
    const fileName = `billing_report_${reportMode}_${selectedStaffId === "all" ? "all_staff" : String(selectedStaffId).replace(/\s+/g, "_")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  async function handleSync() {
    const confirm = await Swal.fire({
      title: "Sync weekly billing data?",
      text: `Pull billing records for ${formatDate(syncWeekStart)} to ${formatDate(getSundayOf(syncWeekStart))}. Monthly reports are assembled from these weekly rows.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sync",
    });

    if (!confirm.isConfirmed) return;

    setSyncing(true);
    try {
      const res = await axios.post(
        `${baseurladdress}billing/sync?week_start=${syncWeekStart}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { fetched, inserted, status } = res.data;
      await Swal.fire({
        icon: status === "success" ? "success" : "warning",
        title: `Sync ${status}`,
        text: `Fetched: ${fetched} records. New inserts: ${inserted}.`,
      });

      if (reportMode === "weekly") {
        await loadWeeklyReport(selectedWeekStart, selectedStaffId);
      } else {
        await loadMonthlyReport(selectedMonth, selectedStaffId);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Sync failed",
        text: err?.response?.data?.message ?? "Server error",
      });
    } finally {
      setSyncing(false);
    }
  }

  async function exportPdf() {
    if (!reportData?.staff?.length) return;

    setExporting(true);
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      const chartGap = 8;
      const chartWidth = (pageWidth - margin * 2 - chartGap) / 2;

      const addHeader = () => {
        doc.setFillColor(15, 118, 110);
        doc.rect(0, 0, pageWidth, 23, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Billing Report", margin, 14);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin, 14, { align: "right" });
      };

      const addFooter = (pageNumber) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: "right" });
      };

      addHeader();

      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(reportMode === "monthly" ? "Monthly Billing Summary" : "Weekly Billing Summary", margin, 32);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Scope: ${staffLabel}`, margin, 39);
      doc.text(`Period: ${reportData.periodLabel}`, margin, 45);
      doc.text(`Mode: ${reportMode}`, margin, 51);
      doc.text(`Staff count: ${formatCount(summary.staff_count)}`, 150, 39);
      doc.text(`Doctor count: ${formatCount(summary.doctor_count)}`, 150, 45);
      doc.text(`Weeks covered: ${formatCount(summary.week_count)}`, 150, 51);

      autoTable(doc, {
        startY: 58,
        head: [["Metric", "Value", "Metric", "Value"]],
        body: [
          ["Total amount", formatCurrency(summary.total_amount), "Total bills", formatCount(summary.total_bills)],
          ["Average bill", formatCurrency(summary.average_bill_value), "Report mode", reportMode === "monthly" ? "Monthly" : "Weekly"],
          ["Scope", staffLabel, "Period", reportData.periodLabel],
        ],
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [15, 118, 110] },
        margin: { left: margin, right: margin },
      });

      const chartY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : 80;
      const leftSeries = comparisonSeries.length > 0 ? comparisonSeries : reportData.staff.slice(0, 6).map((staff, index) => ({
        label: staff.staff_name,
        value: numericValue(staff.total_amount),
        color: CHART_COLORS[index % CHART_COLORS.length],
        meta: formatCount(staff.total_bills),
      }));
      const rightSeries = trendSeries.length > 0 ? trendSeries : [];

      drawBarPanel(doc, {
        x: margin,
        y: chartY,
        width: chartWidth,
        title: selectedStaffId === "all" ? "Staff totals" : "Doctor totals",
        subtitle: selectedStaffId === "all"
          ? "Highest billing staff in the current view"
          : "Doctor-level totals for the selected staff member",
        series: leftSeries,
        emptyText: "No staff rows were available for this report.",
      });

      drawBarPanel(doc, {
        x: margin + chartWidth + chartGap,
        y: chartY,
        width: chartWidth,
        title: reportMode === "monthly" ? "Weekly trend" : "Service mix",
        subtitle: reportMode === "monthly"
          ? "Each bar represents a billing week in the month"
          : "Service amounts for the selected weekly report",
        series: rightSeries,
        emptyText: reportMode === "monthly"
          ? "No weekly aggregates were available for this month."
          : "No service totals were available for this week.",
      });

      const chartBottom = chartY + 82;

      if (reportMode === "monthly" && reportData.weeks.length > 0) {
        autoTable(doc, {
          startY: chartBottom + 2,
          head: [["Week", "Bills", "Amount"]],
          body: reportData.weeks.map((week) => [
            `${formatShortDate(week.week_start)} - ${formatShortDate(week.week_end)}`,
            formatCount(week.total_bills),
            formatCurrency(week.total_amount),
          ]),
          theme: "striped",
          styles: { fontSize: 8.5, cellPadding: 2.2 },
          headStyles: { fillColor: [30, 41, 59] },
          margin: { left: margin, right: margin },
        });
      } else {
        autoTable(doc, {
          startY: chartBottom + 2,
          head: [["Service", "Count", "Amount"]],
          body: [
            ["PET", formatCount(reportData.serviceTotals.pet_count), formatCurrency(reportData.serviceTotals.pet_amount)],
            ["CT", formatCount(reportData.serviceTotals.ct_count), formatCurrency(reportData.serviceTotals.ct_amount)],
            ["SPECT", formatCount(reportData.serviceTotals.spect_count), formatCurrency(reportData.serviceTotals.spect_amount)],
            ["Therapy", formatCount(reportData.serviceTotals.therapy_count), formatCurrency(reportData.serviceTotals.therapy_amount)],
            ["EECP", formatCount(reportData.serviceTotals.eecp_count), formatCurrency(reportData.serviceTotals.eecp_amount)],
          ],
          theme: "striped",
          styles: { fontSize: 8.5, cellPadding: 2.2 },
          headStyles: { fillColor: [30, 41, 59] },
          margin: { left: margin, right: margin },
        });
      }

      doc.addPage();
      addHeader();

      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Staff Summary", margin, 32);

      autoTable(doc, {
        startY: 36,
        head: [["Staff Code", "Staff Name", "Doctors", "Total Bills", "Total Amount"]],
        body: reportData.staff.map((staff) => [
          staff.staff_code || "-",
          staff.staff_name || "-",
          formatCount(staff.doctors?.length || 0),
          formatCount(staff.total_bills),
          formatCurrency(staff.total_amount),
        ]),
        theme: "striped",
        styles: { fontSize: 8.5, cellPadding: 2.2 },
        headStyles: { fillColor: [15, 118, 110] },
        margin: { left: margin, right: margin },
      });

      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Doctor Breakdown", margin, doc.lastAutoTable.finalY + 10);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 14,
        head: [["Staff", "Doctor", "Hospital", "Bills", "Amount", "Tests", "PET", "CT", "SPECT", "Therapy", "EECP"]],
        body: reportData.staff.flatMap((staff) => (
          (staff.doctors || []).map((doctor) => [
            staff.staff_name || "-",
            doctor.doctor_name || "-",
            formatDoctorHospitalLabel(doctor) || "-",
            formatCount(doctor.total_bills),
            formatCurrency(doctor.total_amount),
            formatCount(doctor.test_count),
            formatCount(doctor.pet_count),
            formatCount(doctor.ct_count),
            formatCount(doctor.spect_count),
            formatCount(doctor.therapy_count),
            formatCount(doctor.eecp_count),
          ])
        )),
        theme: "striped",
        styles: { fontSize: 7.4, cellPadding: 1.8 },
        headStyles: { fillColor: [30, 41, 59] },
        margin: { left: margin, right: margin },
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        addFooter(page);
      }

      const scopeSlug = selectedStaffId === "all" ? "all_staff" : String(selectedStaffId).replace(/\s+/g, "_");
      const periodSlug = reportMode === "monthly" ? selectedMonth : selectedWeekStart;
      doc.save(`billing_report_${reportMode}_${scopeSlug}_${periodSlug}.pdf`);
    } finally {
      setExporting(false);
    }
  }

  const serviceCards = [
    { label: "PET", count: serviceTotals.pet_count, amount: serviceTotals.pet_amount },
    { label: "CT", count: serviceTotals.ct_count, amount: serviceTotals.ct_amount },
    { label: "SPECT", count: serviceTotals.spect_count, amount: serviceTotals.spect_amount },
    { label: "Therapy", count: serviceTotals.therapy_count, amount: serviceTotals.therapy_amount },
    { label: "EECP", count: serviceTotals.eecp_count, amount: serviceTotals.eecp_amount },
  ];

  const viewTitle = reportMode === "monthly" ? "Monthly Billing Report" : "Weekly Billing Report";
  const periodCaption = reportMode === "monthly"
    ? `${getMonthLabel(selectedMonth)}`
    : `${formatDate(selectedWeekStart)} to ${formatDate(getSundayOf(selectedWeekStart))}`;

  return (
    <div
      style={{
        padding: 24,
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef6f4 100%)",
      }}
    >
      <div style={{ maxWidth: 1480, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <Card
          style={{
            border: "none",
            borderRadius: 22,
            overflow: "hidden",
            background: "linear-gradient(135deg, #0f766e 0%, #115e59 55%, #134e4a 100%)",
            color: "white",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)",
          }}
        >
          <Card.Body style={{ padding: 24 }}>
            <Row className="g-3 align-items-center">
              <Col lg={8}>
                <div style={{ opacity: 0.82, fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5 }}>
                  Billing analytics
                </div>
                <h2 style={{ margin: "8px 0 10px", fontWeight: 800 }}>{viewTitle}</h2>
                <div style={{ maxWidth: 860, fontSize: 14, lineHeight: 1.7, opacity: 0.95 }}>
                  Billing sync still runs week by week, but the web report can be shown as a weekly view or as a
                  monthly rollup assembled from the synced weekly rows. The same dataset powers the on-screen charts
                  and the exported PDF.
                </div>
              </Col>
              <Col lg={4}>
                <div
                  style={{
                    borderRadius: 18,
                    padding: 16,
                    background: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                >
                  <div style={{ fontSize: 12, opacity: 0.85, textTransform: "uppercase", letterSpacing: 1 }}>
                    Current view
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{staffLabel}</div>
                  <div style={{ marginTop: 8, fontSize: 13, opacity: 0.92 }}>{periodCaption}</div>
                  <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "6px 10px" }}>
                      {reportMode === "monthly" ? "Monthly rollup" : "Weekly source data"}
                    </Badge>
                    <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "6px 10px" }}>
                      {selectedStaffId === "all" ? "All staff" : "Single staff"}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)" }}>
          <Card.Body style={{ padding: 20 }}>
            <Row className="g-3 align-items-end">
              <Col md={3} lg={2}>
                <Form.Label>Report mode</Form.Label>
                <Form.Select value={reportMode} onChange={(e) => setReportMode(e.target.value)}>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Form.Select>
              </Col>

              {reportMode === "weekly" ? (
                <Col md={3} lg={2}>
                  <Form.Label>Weekly report date</Form.Label>
                  <Form.Control
                    type="date"
                    value={selectedWeekStart}
                    onChange={(e) => setSelectedWeekStart(getMondayOf(e.target.value))}
                  />
                  <Form.Text className="text-muted">Snaps to the Monday of the chosen week.</Form.Text>
                </Col>
              ) : (
                <Col md={3} lg={2}>
                  <Form.Label>Reporting month</Form.Label>
                  <Form.Control
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                  <Form.Text className="text-muted">Rolls up the synced weekly rows in that month.</Form.Text>
                </Col>
              )}

              <Col md={3} lg={2}>
                <Form.Label>Sync week</Form.Label>
                <Form.Control
                  type="date"
                  value={syncWeekStart}
                  onChange={(e) => setSyncWeekStart(getMondayOf(e.target.value))}
                />
                <Form.Text className="text-muted">Used only for the weekly sync action.</Form.Text>
              </Col>

              <Col md={3} lg={2}>
                <Form.Label>Staff scope</Form.Label>
                <Form.Select value={selectedStaffId} onChange={(e) => setSelectedStaffId(e.target.value)}>
                  <option value="all">All Staff</option>
                  {staffList.map((staff) => (
                    <option key={staff.staff_id} value={String(staff.staff_id)}>
                      {staff.staff_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={12} lg={4} className="d-flex gap-2 justify-content-lg-end flex-wrap">
                <Button size="sm" variant="outline-success" onClick={handleSync} disabled={syncing}>
                  {syncing ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Icon.ArrowRepeat className="me-1" />
                      Sync Weekly Data
                    </>
                  )}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={handleExportExcel} disabled={!reportData?.staff?.length}>
                  <Icon.FileEarmarkArrowDown className="me-1" />
                  Export Excel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={exportPdf}
                  disabled={!reportData?.staff?.length || exporting}
                  style={{ background: "#0f766e", borderColor: "#0f766e" }}
                >
                  {exporting ? "Preparing PDF..." : "Download PDF"}
                </Button>
              </Col>
            </Row>

            <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
              <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "8px 10px" }}>
                {reportMode === "monthly" ? "Monthly report assembled from weekly records" : "Weekly report from synced DB rows"}
              </Badge>
              <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "8px 10px" }}>
                {reportData?.weeks?.length ? `${reportData.weeks.length} week(s) included` : "No weekly data loaded yet"}
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Row className="g-3">
          <Col xs={12} sm={6} xl={4}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Total amount</div>
                <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: "#0f766e" }}>{formatCurrency(summary.total_amount)}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  Billing value across the current report period.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={4}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Total bills</div>
                <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: "#1d4ed8" }}>{formatCount(summary.total_bills)}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  Bill rows included after the weekly aggregation.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={4}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Average bill</div>
                <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: "#7c3aed" }}>{formatCurrency(summary.average_bill_value)}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  Average amount per bill in the current report.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={3}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Staff count</div>
                <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: "#dc2626" }}>{formatCount(summary.staff_count)}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  Staff rows after the selected scope filter.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={3}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Doctor count</div>
                <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: "#0891b2" }}>{formatCount(summary.doctor_count)}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  Unique doctor entries in the current dataset.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={3}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Weeks included</div>
                <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: "#0f766e" }}>{formatCount(summary.week_count)}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  Weekly slices used to assemble the current report.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={6} xl={3}>
            <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
              <Card.Body>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Scope</div>
                <div style={{ marginTop: 10, fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{staffLabel}</div>
                <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  {reportData?.periodLabel || "-"}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg={6}>
            <ChartCard
              title={selectedStaffId === "all" ? "Staff comparison" : "Doctor comparison"}
              subtitle={selectedStaffId === "all"
                ? "Largest staff totals in the current report"
                : "Doctor totals for the selected staff member"}
              series={comparisonSeries}
              emptyText="No comparison data available for the current filters."
            />
          </Col>
          <Col lg={6}>
            <ChartCard
              title={reportMode === "monthly" ? "Weekly trend" : "Service mix"}
              subtitle={reportMode === "monthly"
                ? "Billing totals per synced week"
                : "Service amounts for the selected week"}
              series={trendSeries}
              emptyText={reportMode === "monthly"
                ? "No weekly trend data available for this month."
                : "No service mix data available for this week."}
            />
          </Col>
        </Row>

        <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)" }}>
          <Card.Body style={{ padding: 18 }}>
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <div>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Detail panel</div>
                <h5 style={{ margin: "4px 0 0", fontWeight: 800 }}>
                  {reportMode === "monthly" ? "Monthly rollup details" : "Weekly billing details"}
                </h5>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {serviceCards.map((service, index) => (
                  <Badge
                    key={service.label}
                    bg="light"
                    text="dark"
                    style={{
                      borderRadius: 999,
                      padding: "8px 10px",
                      border: `1px solid rgba(${CHART_COLORS[index % CHART_COLORS.length].join(",")}, 0.18)`,
                    }}
                  >
                    {service.label}: {formatCount(service.count)} / {formatCurrency(service.amount)}
                  </Badge>
                ))}
              </div>
            </div>

            {reportMode === "monthly" && reportData.weeks.length > 0 ? (
              <div className="mt-3">
                <Table responsive bordered hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Week</th>
                      <th>Staff</th>
                      <th className="text-end">Bills</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.weeks.map((week) => (
                      <tr key={`${week.week_start}-${week.week_end}`}>
                        <td>{formatDate(week.week_start)} to {formatDate(week.week_end)}</td>
                        <td>{formatCount(week.staff_count)}</td>
                        <td className="text-end">{formatCount(week.total_bills)}</td>
                        <td className="text-end fw-semibold">{formatCurrency(week.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : null}
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : !reportData?.staff?.length ? (
          <div className="text-center text-muted py-5">
            <Icon.InboxFill size={32} className="mb-2 d-block mx-auto" />
            No billing data found for the selected period.
          </div>
        ) : (
          <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)" }}>
            <Card.Body style={{ padding: 0 }}>
              <div style={{ padding: "14px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontWeight: 700 }}>
                Staff and doctor breakdown
              </div>
              <Table responsive bordered hover size="sm" className="mb-0 align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: 36 }} />
                    <th>Staff Code</th>
                    <th>Staff Name</th>
                    <th className="text-center">Doctors</th>
                    <th className="text-end">Total Bills</th>
                    <th className="text-end">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.staff.map((staff) => (
                    <Fragment key={`staff-group-${staff.staff_id}`}>
                      <tr
                        key={`staff-${staff.staff_id}`}
                        className="table-active"
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleExpand(staff.staff_id)}
                      >
                        <td className="text-center">
                          {expanded[staff.staff_id] ? <Icon.ChevronDown size={12} /> : <Icon.ChevronRight size={12} />}
                        </td>
                        <td>{staff.staff_code}</td>
                        <td className="fw-semibold">{staff.staff_name}</td>
                        <td className="text-center">
                          <Badge bg="secondary">{formatCount(staff.doctors?.length || 0)}</Badge>
                        </td>
                        <td className="text-end">{formatCount(staff.total_bills)}</td>
                        <td className="text-end fw-semibold">{formatCurrency(staff.total_amount)}</td>
                      </tr>

                      {expanded[staff.staff_id] && (staff.doctors || []).map((doctor) => (
                        <Fragment key={`doc-group-${staff.staff_id}-${doctor.doctor_id}`}>
                          <tr key={`doc-${staff.staff_id}-${doctor.doctor_id}`}>
                            <td />
                            <td colSpan={2} className="ps-4 text-muted small">
                              <Icon.PersonCircle className="me-1" />
                              {doctor.doctor_name}
                              {formatDoctorHospitalLabel(doctor) ? (
                                <Badge bg="light" text="dark" className="ms-2 border">
                                  {formatDoctorHospitalLabel(doctor)}
                                </Badge>
                              ) : null}
                            </td>
                            <td className="text-center">
                              <Badge bg="info">{formatCount(doctor.test_count)}</Badge>
                            </td>
                            <td className="text-end small">{formatCount(doctor.total_bills)}</td>
                            <td className="text-end small">{formatCurrency(doctor.total_amount)}</td>
                          </tr>

                          <tr className="table-light">
                            <td />
                            <td colSpan={5} className="p-2">
                              <Collapse in={!!expanded[staff.staff_id]}>
                                <div>
                                  <div className="d-flex flex-wrap gap-2 mb-2 small">
                                    <span className="badge bg-primary-subtle text-primary border">
                                      Total bills: {formatCount(doctor.total_bills)}
                                    </span>
                                    <span className="badge bg-secondary-subtle text-secondary border">
                                      Total amount: {formatCurrency(doctor.total_amount)}
                                    </span>
                                    <span className="badge bg-light text-dark border">
                                      Tests: {formatCount(doctor.test_count)}
                                    </span>
                                  </div>

                                  <div className="row g-2">
                                    <div className="col-12 col-md-4 col-lg-2">
                                      <div className="border rounded bg-white p-2 h-100">
                                        <div className="text-muted small">PET</div>
                                        <div className="fw-semibold">{formatCount(doctor.pet_count)}</div>
                                        <div className="small text-muted">{formatCurrency(doctor.pet_amount)}</div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-lg-2">
                                      <div className="border rounded bg-white p-2 h-100">
                                        <div className="text-muted small">CT</div>
                                        <div className="fw-semibold">{formatCount(doctor.ct_count)}</div>
                                        <div className="small text-muted">{formatCurrency(doctor.ct_amount)}</div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-lg-2">
                                      <div className="border rounded bg-white p-2 h-100">
                                        <div className="text-muted small">SPECT</div>
                                        <div className="fw-semibold">{formatCount(doctor.spect_count)}</div>
                                        <div className="small text-muted">{formatCurrency(doctor.spect_amount)}</div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-lg-2">
                                      <div className="border rounded bg-white p-2 h-100">
                                        <div className="text-muted small">Therapy</div>
                                        <div className="fw-semibold">{formatCount(doctor.therapy_count)}</div>
                                        <div className="small text-muted">{formatCurrency(doctor.therapy_amount)}</div>
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-lg-2">
                                      <div className="border rounded bg-white p-2 h-100">
                                        <div className="text-muted small">EECP</div>
                                        <div className="fw-semibold">{formatCount(doctor.eecp_count)}</div>
                                        <div className="small text-muted">{formatCurrency(doctor.eecp_amount)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}

                  <tr className="table-dark fw-bold">
                    <td colSpan={4} className="text-end">Grand Total</td>
                    <td className="text-end">{formatCount(summary.total_bills)}</td>
                    <td className="text-end">{formatCurrency(summary.total_amount)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, series, emptyText }) {
  const items = Array.isArray(series) ? series.slice(0, 8) : [];
  const max = Math.max(...items.map((item) => numericValue(item.value)), 1);

  return (
    <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)", height: "100%" }}>
      <Card.Body style={{ padding: 18 }}>
        <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>{title}</div>
        <div style={{ marginTop: 4, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>{subtitle}</div>

        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          {items.length === 0 ? (
            <div style={{ padding: "26px 14px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 14, border: "1px dashed #cbd5e1" }}>
              {emptyText}
            </div>
          ) : (
            items.map((item, index) => {
              const width = `${Math.max(3, (numericValue(item.value) / max) * 100)}%`;
              const color = item.color || CHART_COLORS[index % CHART_COLORS.length];

              return (
                <div key={`${title}-${item.label}-${index}`} style={{ display: "grid", gap: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{item.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
                      {item.meta ? `${item.meta} • ` : ""}
                      {formatCurrency(item.value)}
                    </div>
                  </div>
                  <div style={{ height: 10, borderRadius: 999, background: "#e2e8f0", overflow: "hidden" }}>
                    <div
                      style={{
                        width,
                        height: "100%",
                        borderRadius: 999,
                        background: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
                        boxShadow: "0 8px 14px rgba(15, 23, 42, 0.10)",
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

export default BillingReport;
